import React, { useEffect, useMemo, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Modal, Button } from '@repo/ui'
import { validateEmailAddress } from '@repo/types'

import { UserChecklist, EmailChipInput } from 'components/input'

import type { SignupEntry } from 'hooks/api/useSignups'

/**
 * VRG-Pricing.Access.Gate — add addresses to the signup allowlist.
 *
 * ## Why this is a modal at all
 *
 * The Allowlist tab used to carry a bare email input sitting beside its "Add to
 * allowlist" button, permanently, in the tab row's action slot. That control could
 * do exactly one thing — one address, typed by hand — which is the wrong shape for
 * the job it is actually asked to do: most of the addresses an operator wants to
 * allow are ALREADY on this page, one tab to the left, on the waitlist. Letting
 * them in meant reading an address off the Waitlist tab and retyping it here.
 *
 * So the button opens a composer instead, and the composer offers both halves:
 * pick people who already asked, or type addresses that never did.
 *
 * ## What this reuses, and from where (§28/§36/§47)
 *
 * NOTHING here is a new interaction. Both halves already existed:
 *
 * | Half | Component | Established by |
 * |---|---|---|
 * | search + multi-select checklist | `UserChecklist` | the §28 canonical `AssignUsersModal`, extracted in VRG-565 |
 * | type an address with no record behind it | `EmailChipInput` | the transfer-to-owner flow's invite row |
 *
 * `UserChecklist` is `AssignUsersModal`'s behaviour verbatim — its own docblock
 * records that it was extracted precisely so a fifth copy would not be written, and
 * this would have been the fifth. It brings the §19 skeleton with it.
 *
 * The free-type half is the same capability `TransferCertificateModal` exposes for
 * a recipient who has no `Customer` row: an address is a legitimate target even
 * when nothing in the system knows it yet. That modal reaches it through
 * `SearchFilter`'s `listFooter` because it takes exactly ONE recipient; this takes
 * many, so the chip field is the right shape — but the validator underneath is the
 * same `validateEmailAddress` from `@repo/types`, deliberately, so an ending this
 * app refuses in one place it refuses in the other.
 *
 * ## Duplicates are REFUSED WITH A REASON, never absorbed
 *
 * Three ways to say the same address twice, and all three are caught before submit:
 * it is already on the allowlist, it is already chipped, or it is already ticked in
 * the list above. Each gets its own sentence, because "already added" would leave
 * the operator hunting for where.
 *
 * The API is idempotent, so a silent duplicate would be harmless and INVISIBLE —
 * which is the problem. An operator who types an address that is already allowed
 * and sees it vanish into a submit cannot tell "added" from "was already there",
 * and those are different answers to the question they were asking.
 */

interface AddToAllowlistModalProps {
  open: boolean
  onClose: () => void
  /** The waitlist, unfiltered. Rows already on the allowlist are excluded here. */
  waitlist: SignupEntry[]
  isWaitlistLoading?: boolean
  /** Addresses already on the allowlist, in whatever case they were stored. */
  existingEmails: string[]
  /** Runs the adds. Resolves when every address has landed (or the first one failed). */
  onSubmit: (emails: string[]) => Promise<void> | void
  isSubmitting?: boolean
}

export const AddToAllowlistModal = ({
  open,
  onClose,
  waitlist,
  isWaitlistLoading = false,
  existingEmails,
  onSubmit,
  isSubmitting = false,
}: AddToAllowlistModalProps) => {
  /** Signup ids ticked in the checklist. The `Set` is `UserChecklist`'s own contract. */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  /** Addresses typed by hand, already committed to chips. */
  const [typedEmails, setTypedEmails] = useState<string[]>([])
  /**
   * Text sitting in the chip field that has NOT become a chip.
   *
   * Load-bearing for the submit gate. Without it an address the field refused is
   * invisible from out here, and pressing Add drops it silently — the field says
   * why it was refused, but nothing stops the operator submitting past the message.
   */
  const [pendingEmail, setPendingEmail] = useState('')
  /**
   * The submit loop is running.
   *
   * ⚠️ NOT redundant with `isSubmitting`. That prop is one mutation's `isPending`, and the
   * page adds addresses one at a time — so between the first and the second it is briefly
   * FALSE, which re-enables this button mid-run and makes a second click start a second
   * loop. A flag that spans the whole loop is the only thing that covers the gap.
   */
  const [isRunning, setIsRunning] = useState(false)
  const busy = isSubmitting || isRunning

  // Reset on CLOSE rather than on open, so the reset runs once the dialog is gone
  // and the user never sees the list empty itself underneath them.
  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set())
      setTypedEmails([])
      setPendingEmail('')
      setIsRunning(false)
    }
  }, [open])

  /**
   * Allowlist membership, lowercased once.
   *
   * Addresses are compared case-insensitively everywhere in this modal because the
   * allowlist stores whatever was typed: `Ana@Brand.com` and `ana@brand.com` are
   * the same mailbox, and a duplicate check that missed that would be a duplicate
   * check that misses the commonest duplicate.
   */
  const existingSet = useMemo(
    () => new Set(existingEmails.map(email => email.toLowerCase())),
    [existingEmails]
  )

  /**
   * Waitlist rows that are not already allowed.
   *
   * Filtered rather than shown-and-disabled: this is record state, and §46 hides
   * record state. "Already on the allowlist" also has no resolving action to offer
   * beside it — they are in — so a disabled row would be a tombstone.
   */
  const candidates = useMemo(
    () => waitlist.filter(row => row.email && !existingSet.has(row.email.toLowerCase())),
    [waitlist, existingSet]
  )

  const selectedEmails = useMemo(
    () =>
      candidates
        .filter(row => selectedIds.has(row.id))
        .map(row => row.email)
        .filter(Boolean),
    [candidates, selectedIds]
  )

  const selectedSet = useMemo(
    () => new Set(selectedEmails.map(email => email.toLowerCase())),
    [selectedEmails]
  )

  /**
   * The verdict on an address the operator is trying to chip.
   *
   * Order matters: the syntax question is asked first because a malformed string
   * cannot meaningfully be a duplicate of anything, and answering "already on the
   * allowlist" about `ana@` would be nonsense.
   *
   * ⚠️ `validateEmailAddress`, not a local regex. VRG-471 records what a
   * syntax-only check cost: `spencer@usevergence.cin` passed, provisioned an owner
   * and sent two undeliverable emails. `.cin` is syntactically perfect and is not a
   * delegated TLD, and only the shared table knows that.
   */
  const validateTyped = (email: string): string | null => {
    const result = validateEmailAddress(email)
    if (!result.ok) return result.message ?? 'Enter a valid email address.'

    const lower = email.toLowerCase()
    if (existingSet.has(lower)) return `${email} is already on the allowlist.`
    if (selectedSet.has(lower)) return `${email} is already selected from the waitlist above.`
    // A repeat of an existing chip is caught by `EmailChipInput` itself, which owns
    // that list — re-checking it here would be two answers to one question.
    return null
  }

  const toggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  /**
   * Everything about to be submitted, de-duplicated case-insensitively.
   *
   * The de-dupe is belt-and-braces: `validateTyped` already refuses an overlap
   * between the two halves. It stays because the two halves can also cross AFTER a
   * chip is committed — tick a waitlist row whose address you had already typed —
   * and that ordering has no commit point to validate at.
   */
  const emailsToAdd = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const email of [...selectedEmails, ...typedEmails]) {
      const lower = email.toLowerCase()
      if (seen.has(lower)) continue
      seen.add(lower)
      out.push(email)
    }
    return out
  }, [selectedEmails, typedEmails])

  /*
    §54 — live only when there is something VALID to submit.

    Two conditions, and the second is the one that is easy to miss: a rejected
    address still sitting in the chip field means the operator asked for something
    this modal is not going to do. Submitting past it would drop it silently, which
    is the exact outcome the rejection message exists to prevent — the message
    explains, the gate enforces.

    A blur commits whatever is valid before this is read, so a typed-and-not-Entered
    address is chipped by the time the click lands. What remains pending is only ever
    something that was refused.
  */
  const canSubmit = emailsToAdd.length > 0 && pendingEmail.trim() === '' && !busy

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsRunning(true)
    try {
      await onSubmit(emailsToAdd)
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <Modal
      title="Add to allowlist"
      open={open}
      onClose={onClose}
      actions={
        <>
          {/*
            §54 — Cancel is never disabled. It is the exit, and an exit always
            exists. Closing mid-submit races nothing: the mutations are owned by the
            page, not by this modal, so they complete and the list invalidates
            whether the dialog is open or not.
          */}
          <Button variant="secondary" disableElevation onClick={onClose}>
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={busy}
          >
            {emailsToAdd.length > 1 ? `Add ${emailsToAdd.length}` : 'Add'}
          </Button>
        </>
      }
    >
      <Stack spacing={5}>
        <Stack spacing={3}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Pick people who have already asked for access. Anyone added here can create a Vera
            account. No email is sent. Use &quot;Send signup link&quot; on the row afterwards to
            tell them.
          </Typography>

          {/*
            §28/§36 — the shared checklist, not a fifth hand-roll of the same
            search-box-over-scrollable-checkbox-rows pattern. It carries the §19
            skeleton and the row/checkbox click reconciliation with it.
          */}
          <UserChecklist
            users={candidates}
            selectedIds={selectedIds}
            onToggle={toggle}
            isLoading={isWaitlistLoading}
            searchPlaceholder="Search the waitlist"
            emptyMessage="Everyone on the waitlist is already on the allowlist."
            noMatchMessage="Nobody on the waitlist matches your search."
          />
        </Stack>

        <Stack spacing={2}>
          {/*
            The other half. Separated by its own label rather than merged into the
            list, because "pick from what we have" and "name something we do not"
            are different acts and a combined control would have to guess which one
            a keystroke meant.
          */}
          <EmailChipInput
            label="Or add addresses that are not on the waitlist"
            value={typedEmails}
            onChange={setTypedEmails}
            validate={validateTyped}
            onPendingChange={setPendingEmail}
            disabled={busy}
          />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Press Enter, comma or space to add each one. You can paste a list.
          </Typography>
        </Stack>
      </Stack>
    </Modal>
  )
}
